// Shared marketing copy for the two landing surfaces:
//   - Desktop Navigator pages: src/components/layout/BrowserWindow.jsx
//   - Mobile landing:          src/components/layout/MobileLanding.jsx
//
// Both files render the same site with different layout/JSX, so every string
// here is content that must stay identical between the two. Layout, inline
// styles, and structure stay in the components.
//
// Divergence policy: where mobile deliberately abbreviates a string (shorter
// stat labels, shorter alt text, trimmed paragraphs), the full/desktop text
// lives here when the surrounding structure is shared, and MobileLanding.jsx
// keeps its abbreviated variant local with a note. Paragraphs that differ
// wholesale between the two surfaces stay local to each file.
//
// Headlines with inline emphasis (<em className="mb-key">, <strong>, mb-figure)
// are stored as string parts (pre / key / post etc.) so each component keeps
// its own markup while the words live in one place.

// Screen-reader-only page <h1> (SEO title) — identical on both surfaces.
export const SEO_H1 =
  "Good Neighbor Fund - $1,000 Micro-Grants for Bold Founders | Chapters in Western New York, Denver, Central NY, the Capital Region, and the Hudson Valley";

/* ===== HOME ===== */

export const HOME_HERO = {
  eyebrow: "Est. 2023 · Belief Capital",
  headline: {
    figure: "$1,000",
    mid: " micro-grants for ",
    key: "bold",
    tail: " business ideas.",
  },
  lede: 'We back founders before they\'re "ready." No pitch deck. No equity taken. Just belief in your vision and potential.',
  cta: "Submit Your Pitch",
};

export const MISSION = {
  eyebrow: "Our Mission",
  headline: "Belief capital for founders at the very beginning.",
  lede: {
    pre: "Good Neighbor Fund is a micro-grant program that gives ",
    strong: "$1,000 in belief capital",
    post: " to under-resourced founders with bold new business ideas.",
  },
  body: {
    pre: "We don't expect a pitch deck. We don't want equity. We fund ",
    em: "you",
    post: ": your idea, your energy, your potential. Our funding doesn't come from banks, VCs, or foundations; it comes from neighbors who chip in their own funds, meet quarterly to review applications, and award the micro-grants together.",
  },
  tagline: "Born in Buffalo, built for neighborhoods everywhere.",
};

export const IMPACT = {
  eyebrow: "Impact · Since 2023",
  headline: { pre: "Small bets. ", key: "Real", post: " neighborhoods." },
};

// Fallback copy for the landing impact stats, shown until the live
// all-chapter totals load from publicWinners (see useLandingImpactStats in
// helpers/impactStats.js, which also reuses these labels). Mobile abbreviates
// the last three labels (see MOBILE_STAT_LABELS in MobileLanding.jsx).
// Values are a snapshot of the live publicWinners aggregate (39 winners as of
// 2026-08-19, hidden ones included — awarded money counts before the winner
// is announced) so the fallback-to-live swap is invisible. Re-snapshot when
// new grants are awarded, or the old numbers flash briefly on load. The same
// all-chapter figures are hardcoded in public/llms.txt, the FAQ JSON-LD of
// public/index.html and each public/<slug>.html, and the social card
// (public/assets/og/templates/og-default.html, rendered to
// public/assets/og/default.png); each chapter page's impact stat fallbacks
// carry that chapter's own numbers. Re-snapshot them together.
export const IMPACT_STATS = [
  { n: "39", label: "Business ideas funded" },
  { n: "69%", label: "Women-owned businesses" },
  { n: "38%", label: "BIPOC-owned businesses" },
  { n: "$38K", label: "In micro-grants awarded" },
];

export const HOW_IT_WORKS = {
  eyebrow: "How It Works",
  headline: "Three steps. No decks, no red tape.",
};

// Mobile abbreviates step 02's copy (see MOBILE_STEP_COPY in MobileLanding.jsx).
export const HOW_IT_WORKS_STEPS = [
  { n: "01", title: "Submit", copy: "Complete our simple online form and upload a 60-second pitch video. That's it. No business plan required." },
  { n: "02", title: "Review", copy: "Each chapter's LPs review every submission at the end of each quarter, together. Every applicant is considered." },
  { n: "03", title: "Award", copy: "Selected founders receive a $1,000 micro-grant with no strings attached. We don't take equity; we take belief." },
];

export const PRESS = {
  eyebrow: "As Featured In",
  headline: "The good word travels.",
};

export const PRESS_ITEMS = [
  {
    title: "Buffalo News",
    url: "https://buffalonews.com/news/local/business/good-neighbor-fund-will-give-1-000-grants-to-entrepreneurs/article_b7c4f284-a900-11ed-ad61-8f0cb76d8c3e.html",
    logo: "/assets/press/buffalo-news.webp"
  },
  {
    title: "Buffalo Rising",
    url: "https://www.buffalorising.com/2023/01/the-good-neighbor-fund-micro-grants-for-start-ups/",
    logo: "/assets/press/buffalo-rising.webp"
  },
  {
    title: "Buffalo Business Journal",
    url: "https://www.bizjournals.com/buffalo/inno/stories/news/2023/01/20/good-neighbor-fund-micro-grant-program-launches.html",
    logo: "/assets/press/buffalo-business.webp"
  },
  {
    title: "Invest Buffalo Niagara",
    url: "https://podcasts.apple.com/us/podcast/jason-bartz-on-good-neighbor-fund/id1260713044?i=1000618789834",
    logo: "/assets/press/invest-buffalo.webp"
  },
  {
    title: "Denver Post",
    url: "https://www.denverpost.com/2023/02/21/denver-microgrants-entrepreneurs-good-neighbor-fund/",
    logo: "/assets/press/denver-post.webp"
  },
  {
    title: "Colorado Sun",
    url: "https://coloradosun.com/2023/08/05/denver-venture-capital-impact-investment/",
    logo: "/assets/press/colorado-sun.webp"
  },
  {
    title: "WIVB",
    url: "https://www.youtube.com/watch?v=7Q0joMyXrbA",
    logo: "/assets/press/wivb.png"
  }
];

export const AWARDEES_MARQUEE_HEAD = {
  // MobileLanding appends " · <n> strong", n being the live funded count from
  // useLandingImpactStats, so the figure can't drift from the impact stats.
  eyebrow: "Portfolio",
  headline: "Neighborhood dreams, at work.",
};

// One entry per awardee; each component handles its own seamless-loop
// duplication. Mobile abbreviates the alt text (see MOBILE_AWARDEE_ALTS in
// MobileLanding.jsx).
export const AWARDEES_MARQUEE = [
  { src: "/assets/Ernies2.webp",   alt: "Ernie's Pop Shop - Buffalo small business $1,000 grant winner from Good Neighbor Fund" },
  { src: "/assets/BFR2.webp",      alt: "Buffalo Fashion Runway - Western New York fashion startup funded by Good Neighbor Fund micro-grant" },
  { src: "/assets/Trinas2.webp",   alt: "Trina's Speedy Cleaning - Buffalo cleaning service business funded by Good Neighbor Fund $1,000 grant" },
  { src: "/assets/gnf-kamil.webp", alt: "Signing is Art - Buffalo-based small business funded by Good Neighbor Fund micro-grant program" },
  { src: "/assets/muuvya.webp",    alt: "Muuvya - Small business funded by Good Neighbor Fund $1,000 small business grant" },
];

export const TESTIMONIAL = {
  eyebrow: "In Their Words",
  quote: "The grant was far more than a financial contribution to jump-starting my business. It provided validation for an idea & passion I've had for some time, support and encouragement to realize a dream of entrepreneurship after a 22-year teaching career.",
  name: "Tracy Csavina",
  role: "Founder · Sustainably Rooted LLC",
};

// The lede differs between surfaces (mobile keeps only the last sentence) and
// stays local to each component.
export const FINAL_CTA = {
  eyebrow: "Apply Now · Q2 2026",
  headline: { pre: "Ready to bring your business idea ", key: "to life?" },
  cta: "Submit Your Pitch",
};

export const FOOTER_LINKS = {
  linkedin: "https://www.linkedin.com/company/good-neighbor-fund",
  instagram: "https://www.instagram.com/goodneighborfund/",
  newsletter: "https://goodneighbors.substack.com/",
  terms: "/terms",
  privacy: "/privacy",
};

export const COPYRIGHT_NOTICE = "GOOD NEIGHBOR FUND · ALL RIGHTS RESERVED";

/* ===== CHAPTERS ===== */

// Hardcoded chapter list preserved as fallback copy for the legacy chapters.
// Used when the /chapters Firestore collection hasn't loaded yet (or is
// empty). Once the collection is populated, the dynamic list wins.
export const LEGACY_CHAPTER_CARDS = [
  { name: 'Western New York', foundedYear: 2023, tagline: 'Where it all started, serving Buffalo and the surrounding 8 counties.',                                                         pageSlug: 'wny' },
  { name: 'Denver',           foundedYear: 2023, tagline: 'Serving the greater Denver metropolitan area.',                                                                                 pageSlug: 'denver' },
  { name: 'Central New York', foundedYear: 2026, tagline: 'Bringing belief capital to founders across Central New York: Syracuse, Ithaca, Binghamton, Utica and beyond.',                  pageSlug: 'upstate' },
  { name: 'Capital Region',   foundedYear: 2026, tagline: "Supporting bold ideas across New York's Capital Region: Albany, Schenectady, Troy and the surrounding area.",                  pageSlug: 'capital-region' },
  { name: 'Hudson Valley',    foundedYear: 2026, tagline: 'Backing founders up and down the Hudson: Westchester to the Catskills, Poughkeepsie to Kingston.',                              pageSlug: 'hudson-valley' },
];

export const CHAPTERS_HERO = {
  eyebrow: "Network · Neighborhood by Neighborhood",
  headline: { pre: "GNF ", key: "Chapters." },
  lede: "We operate through local chapters, each with their own community of Limited Partners who review applications and select quarterly awardees.",
};

export const CHAPTER_CARD_CTAS = {
  visit: "Visit chapter",
  join: "Join as LP",
};

export const HOW_CHAPTERS_WORK = {
  eyebrow: "How Chapters Work",
  headline: "A collective giving organization, at the neighborhood scale.",
  lede: "GNF is a diverse group of founders, operators, and creators who share a passion for entrepreneurship and community. LPs pool their own resources, knowledge, and networks. Each chapter meets quarterly to review applications and select new micro-grant award winners.",
  image: { src: "/assets/molly-brown-cookies.webp", alt: "Chapter community gathering: founders sharing a table over cookies" },
};

// The closing paragraph differs between surfaces (desktop adds two extra
// sentences) and stays local to each component.
export const COLLECTIVE_GIVING = {
  eyebrow: "Model",
  headline: "What is collective giving?",
  lede: "Good Neighbor Fund is more than a grant program; it's a neighborhood of builders and believers. LPs are a diverse collective of founders, operators, and community members who pool their own capital each quarter to fund the boldest new ideas in their chapter.",
  body1: "Each chapter operates as its own independent giving circle, with its own LPs, its own quarterly dinner, and its own awardees. Dollars raised in a chapter stay in that chapter.",
  body2: {
    pre: "There's no overhead. No bureaucracy. We operate on a ",
    strong: "money in, money out",
    post: " model: 100% of LP dues go directly to fund the next wave of local founders.",
  },
  image: { src: "/assets/wny-dinner.webp", alt: "Limited Partners gathered at a WNY chapter dinner to review applications" },
};

export const START_A_CHAPTER = {
  eyebrow: "Expansion · Cities Welcome",
  headline: { pre: "Start a chapter in your ", key: "city." },
  lede: "Interested in launching a GNF chapter in your own community? We're always looking for passionate good neighbors to help spread the belief capital.",
  primaryCta: "Start a Chapter",
  primaryHref: "/start-a-chapter",
  secondaryCta: "Chapter Handbook",
  secondaryHref: "/chapter-handbook",
};

/* ===== AWARDEES ===== */

// The lede differs between surfaces and stays local to each component.
export const AWARDEES_PAGE = {
  eyebrow: "Portfolio",
  headline: "Meet our grant awardees.",
};

/* ===== DONATE ===== */

export const DONATE_HERO = {
  eyebrow: "Support the Fund",
  headline: { pre: "Fuel ", key: "belief capital", post: " for bold neighborhood founders." },
  lede: "We're a 100% volunteer-led organization. Every dollar funds the next micro-grant: no overhead, no middlemen.",
  stat: {
    eyebrow: "1 Grant",
    figure: "$1,000",
    caption: "Your dollars fund a founder, dollar for dollar.",
  },
};

export const GIVING = {
  eyebrow: "Two Ways to Give",
  headline: "Pick a rhythm that fits.",
};

export const ONE_TIME_GIVING = {
  eyebrow: "Option 01 · One-Time",
  headline: "Give once, directly.",
  body: "Your contribution goes straight into the micro-grant fund. Any amount helps: $100 = 10% of a grant, $1,000 = a full grant to a founder.",
  amountsLabel: "Suggested Amounts",
  amounts: ["$25", "$100", "$250", "$1,000"],
  cta: "Donate",
};

// The body paragraph differs between surfaces (mobile drops the last
// sentence) and stays local to each component.
export const CLUB_MEMBERSHIP = {
  badge: "Most Impact",
  eyebrow: "Option 02 · Recurring",
  headline: "Become a GNF Club Member.",
  price: "$25",
  priceUnit: "/ month",
  benefits: [
    "Free access to all GNF-hosted events",
    "Private GNF Slack community",
    "GNF Club stickers",
    "Chapter social-media shoutout",
  ],
  cta: "Join the Club",
  stripeUrl: "https://buy.stripe.com/dR68wO3yCgiVaUo6oq",
};

// The intro paragraph and tax footnote differ between surfaces and stay local.
export const SPONSORSHIP = {
  eyebrow: "Corporate Partnership",
  headline: { line1: "Sponsor a chapter.", line2: "Back a neighborhood." },
  cta: "Inquire About Sponsorship",
  mailto: "mailto:jason@goodneighbor.fund?subject=Corporate%20Sponsorship%20Inquiry",
};

export const SPONSORSHIP_TIERS = [
  { tier: "Neighbor", amount: "$1,000", summary: "Funds one full micro-grant. Name & logo on the awardee's grant announcement." },
  { tier: "Partner", amount: "$5,000", summary: "Funds a full chapter quarter. Event co-branding + logo on chapter page for the year." },
  { tier: "Community Champion", amount: "$10,000+", summary: "Funds a full chapter year. Premier logo placement, named grant round, speaking slot at a chapter dinner." },
];

export const COFOUNDER_NOTE = {
  eyebrow: "A note from the co-founder",
  quote: "Your support fuels our micro-grant program that helps early-stage founders launch their first product, buy initial inventory, or spread the word about their business. These small bets grow into job-creating, community-serving ventures.",
  name: "Jason Bartz",
  role: "CO-FOUNDER · GOOD NEIGHBOR FUND",
  image: { src: "/assets/lps/jason-bartz.webp", alt: "Jason Bartz, co-founder of Good Neighbor Fund" },
};
